import React, { useState } from "react";
import { ZoomIn, ZoomOut, FileText, ExternalLink, ImageOff, X, RotateCcw } from "lucide-react";

interface DocumentPreviewProps {
  imageUrl?: string;
  fileName: string;
}

const ZOOM_LEVELS = [0.1, 0.2, 0.5, 1, 1.5, 2, 3, 5];
const DEFAULT_ZOOM = 0.2;

export function DocumentPreview({ imageUrl, fileName }: DocumentPreviewProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [zoom, setZoom] = useState(DEFAULT_ZOOM);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const handleZoomIn = () => {
    const currentIndex = ZOOM_LEVELS.findIndex((z) => z >= zoom);
    if (currentIndex < ZOOM_LEVELS.length - 1) {
      setZoom(ZOOM_LEVELS[currentIndex + 1]);
    }
  };

  const handleZoomOut = () => {
    const currentIndex = ZOOM_LEVELS.findIndex((z) => z >= zoom);
    if (currentIndex > 0) {
      setZoom(ZOOM_LEVELS[currentIndex - 1]);
    }
  };

  const handleReset = () => {
    setZoom(DEFAULT_ZOOM);
    setPosition({ x: 0, y: 0 });
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging) {
      e.preventDefault();
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = () => setIsDragging(false);

  const openModal = () => {
    setIsModalOpen(true);
    setZoom(DEFAULT_ZOOM);
    setPosition({ x: 0, y: 0 });
  };

  return (
    <>
      <div className="group relative w-full flex flex-col" style={{ minHeight: "300px" }}>
        {/* Chrome / Header */}
        <div className="flex items-center justify-between px-3 py-2 bg-white border border-zinc-200 border-b-0 rounded-t-lg">
          <div className="flex items-center gap-2 min-w-0">
            <div className="p-1 rounded bg-zinc-100 text-zinc-500">
              <FileText className="w-3 h-3" />
            </div>
            <span className="text-[11px] font-medium text-zinc-700 truncate tracking-tight">{fileName}</span>
          </div>
          {imageUrl && (
            <button
              onClick={openModal}
              className="text-zinc-400 hover:text-zinc-600 transition-colors"
              title="View full size"
            >
              <ZoomIn className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* Preview Area */}
        <div
          className="
          relative flex-1 bg-zinc-50/50
          border border-zinc-200 rounded-b-lg
          overflow-hidden group-hover:border-zinc-300
          transition-colors duration-300 cursor-pointer
        "
          style={{ minHeight: "250px" }}
          onClick={imageUrl ? openModal : undefined}
        >
          {/* Image Container */}
          <div className="absolute inset-2 bg-white shadow-sm border border-zinc-100 rounded overflow-hidden flex items-center justify-center">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={`Preview of ${fileName}`}
                className="w-full h-full object-contain transition-transform duration-500 ease-out group-hover:scale-[1.02]"
                onLoad={() => console.log("[DocumentPreview] Image loaded successfully")}
                onError={(e) => console.error("[DocumentPreview] Image failed to load:", e)}
              />
            ) : (
              <div className="flex flex-col items-center gap-2 text-zinc-400">
                <ImageOff className="w-8 h-8" />
                <span className="text-xs">No preview available</span>
              </div>
            )}
          </div>

          {/* Hover Overlay */}
          {imageUrl && (
            <div
              className="
              absolute inset-0 bg-zinc-900/0 group-hover:bg-zinc-900/5
              transition-all duration-300 flex items-center justify-center
              opacity-0 group-hover:opacity-100
            "
            >
              <div
                className="
                bg-white/95 backdrop-blur-sm px-3 py-1.5 rounded-full
                shadow-lg border border-zinc-200/50
                flex items-center gap-2
                transform translate-y-2 group-hover:translate-y-0
                transition-all duration-300 ease-out
              "
              >
                <ZoomIn className="w-3.5 h-3.5 text-zinc-600" />
                <span className="text-[11px] font-medium text-zinc-700">Click to zoom</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Zoom Modal */}
      {isModalOpen && imageUrl && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex flex-col"
          onClick={(e) => {
            if (e.target === e.currentTarget) setIsModalOpen(false);
          }}
        >
          {/* Modal Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-black/50">
            <span className="text-white text-sm font-medium truncate">{fileName}</span>
            <div className="flex items-center gap-2">
              {/* Zoom Controls */}
              <div className="flex items-center gap-1 bg-white/10 rounded-lg px-2 py-1">
                <button
                  onClick={handleZoomOut}
                  className="p-1 text-white/80 hover:text-white transition-colors"
                  title="Zoom out"
                >
                  <ZoomOut className="w-4 h-4" />
                </button>
                <span className="text-white text-xs w-12 text-center">{Math.round(zoom * 100)}%</span>
                <button
                  onClick={handleZoomIn}
                  className="p-1 text-white/80 hover:text-white transition-colors"
                  title="Zoom in"
                >
                  <ZoomIn className="w-4 h-4" />
                </button>
                <button
                  onClick={handleReset}
                  className="p-1 text-white/80 hover:text-white transition-colors ml-1"
                  title="Reset"
                >
                  <RotateCcw className="w-4 h-4" />
                </button>
              </div>
              {/* Close Button */}
              <button
                onClick={() => setIsModalOpen(false)}
                className="p-1.5 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                title="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Image Container */}
          <div
            className="flex-1 overflow-hidden flex items-center justify-center select-none"
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            style={{ cursor: isDragging ? "grabbing" : "grab" }}
          >
            <img
              src={imageUrl}
              alt={`Full view of ${fileName}`}
              className="max-w-none transition-transform duration-200"
              style={{
                transform: `translate(${position.x}px, ${position.y}px) scale(${zoom})`,
              }}
              draggable={false}
            />
          </div>

          {/* Hint */}
          <div className="text-center py-2 text-white/50 text-xs">
            Drag to pan • Use +/- to zoom • Current: {Math.round(zoom * 100)}%
          </div>
        </div>
      )}
    </>
  );
}
